import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, Edit2, Music } from 'lucide-react';
import SpotifySongSearch from '@/components/common/SpotifySongSearch';
import { SpotifyTrack } from '@/services/spotifyService';
import { useSpotifyGenreDetection } from '@/hooks/useSpotifyGenreDetection';
import { GenreSuggestionsList } from '@/components/shared/GenreSuggestionsList';
import { toast } from 'sonner';

interface DisplaySong {
  id?: string;
  title: string;
  artist: string;
  duration?: string;
  year_introduced: number;
  youtube_url?: string;
  display_year_id?: string;
  album_cover_url?: string;  // Track album cover from Spotify
}

interface SongsSectionProps {
  songs: DisplaySong[];
  maxSongs?: number;
  onSongsChange: (songs: DisplaySong[]) => void;
}

const SongsSection: React.FC<SongsSectionProps> = ({
  songs,
  maxSongs = 100,
  onSongsChange,
}) => {
  const currentYear = new Date().getFullYear();
  const [selectedSpotifyTracks, setSelectedSpotifyTracks] = useState<Map<number, SpotifyTrack | null>>(new Map());
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [genreDetectionIndex, setGenreDetectionIndex] = useState<number | null>(null);

  // Use centralized genre detection hook
  const { suggestions, isDetecting, creationStatus, detectGenres } = useSpotifyGenreDetection({
    autoApply: false, // Don't auto-apply for display songs
    maxGenres: 3,
    minConfidence: 0.6,
  });

  // Helper function to check if a song is complete (has required fields)
  const isSongComplete = (song: DisplaySong): boolean => {
    return !!(song.title?.trim() && song.artist?.trim() && song.year_introduced);
  };

  // Reset expandedIndex when songs are loaded/reloaded
  useEffect(() => {
    // Find the first incomplete song to auto-expand
    const firstIncompleteIndex = songs.findIndex(song => !isSongComplete(song));

    // If there's an incomplete song, expand it; otherwise collapse all
    setExpandedIndex(firstIncompleteIndex >= 0 ? firstIncompleteIndex : null);
  }, [songs.length]); // Only trigger when number of songs changes

  const addSong = () => {
    if (songs.length >= maxSongs) return;

    const newSong: DisplaySong = {
      title: '',
      artist: '',
      year_introduced: currentYear,
      youtube_url: ''
    };
    const newIndex = songs.length;
    onSongsChange([...songs, newSong]);
    setExpandedIndex(newIndex); // Auto-expand the new song
  };

  const removeSong = (index: number) => {
    if (songs.length <= 1) return; // Keep at least one song
    const songToRemove = songs[index];
    // Remove by ID if song has one, otherwise by index (for new unsaved songs)
    const newSongs = songToRemove?.id
      ? songs.filter(s => s.id !== songToRemove.id)
      : songs.filter((_, i) => i !== index);
    onSongsChange(newSongs);
  };

  const updateSong = (index: number, updates: Partial<DisplaySong>) => {
    const newSongs = songs.map((song, i) =>
      i === index ? { ...song, ...updates } : song
    );
    onSongsChange(newSongs);
  };


  return (
    <Card>
      <CardHeader>
        <CardTitle>Songs ({songs.length}/{maxSongs})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {songs.map((song, index) => {
          const isExpanded = expandedIndex === index || !isSongComplete(song);
          // Use song ID as key for stable identity, fallback to index for new songs
          const songKey = song.id || `new-song-${index}`;

          return (
            <div key={songKey} className="relative border rounded-lg">
              {/* Collapsed View */}
              {!isExpanded && (
                <div className="flex items-center gap-3 p-3">
                  {/* Album Art Thumbnail */}
                  <div className="flex-shrink-0 w-16 h-16 bg-muted rounded flex items-center justify-center overflow-hidden">
                    {song.album_cover_url ? (
                      <img
                        src={song.album_cover_url}
                        alt={`${song.title} album cover`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Music className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>

                  {/* Song Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {song.title} - {song.artist} ({song.year_introduced})
                    </p>
                    {song.duration && (
                      <p className="text-xs text-muted-foreground">{song.duration}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setExpandedIndex(index)}
                      aria-label="Edit song"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {songs.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeSong(index)}
                        aria-label="Remove song"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Expanded View */}
              {isExpanded && (
                <div className="p-4">
                  <div className="space-y-4">
                    <div>
                      <Label>Find on Spotify</Label>
                      <div className="mt-1">
                        <SpotifySongSearch
                          onTrackSelect={async (trackData, spotifyTrack) => {
                            updateSong(index, {
                              title: trackData.title,
                              artist: trackData.artist,
                              duration: trackData.duration,
                              album_cover_url: trackData.albumCoverUrl
                            });

                            // Detect genres for this track
                            if (spotifyTrack) {
                              setGenreDetectionIndex(index);
                              await detectGenres(spotifyTrack);
                              toast.success('Song details updated from Spotify!');
                            }
                          }}
                          selectedTrack={selectedSpotifyTracks.get(index) || null}
                          onClear={() => {
                            const newTracks = new Map(selectedSpotifyTracks);
                            newTracks.delete(index);
                            setSelectedSpotifyTracks(newTracks);
                            setGenreDetectionIndex(null);
                          }}
                          placeholder="Search for song on Spotify..."
                          showPreview={false}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`song-title-${index}`}>Song Title</Label>
                        <Input
                          id={`song-title-${index}`}
                          value={song.title}
                          onChange={(e) => updateSong(index, { title: e.target.value })}
                          placeholder="Enter song title"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`song-artist-${index}`}>Artist</Label>
                        <Input
                          id={`song-artist-${index}`}
                          value={song.artist}
                          onChange={(e) => updateSong(index, { artist: e.target.value })}
                          placeholder="Enter artist name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`song-duration-${index}`}>Duration (MM:SS)</Label>
                        <Input
                          id={`song-duration-${index}`}
                          value={song.duration || ''}
                          onChange={(e) => updateSong(index, { duration: e.target.value })}
                          placeholder="3:45"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`song-year-${index}`}>Year Introduced</Label>
                        <Input
                          id={`song-year-${index}`}
                          type="number"
                          min="2000"
                          max={currentYear + 1}
                          value={song.year_introduced}
                          onChange={(e) => updateSong(index, { year_introduced: parseInt(e.target.value) || currentYear })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor={`song-youtube-${index}`}>YouTube URL (Optional)</Label>
                      <Input
                        id={`song-youtube-${index}`}
                        value={song.youtube_url || ''}
                        onChange={(e) => updateSong(index, { youtube_url: e.target.value })}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>

                    {/* Genre Detection Results */}
                    {genreDetectionIndex === index && (suggestions.length > 0 || isDetecting || creationStatus.total > 0) && (
                      <div>
                        <Label>Detected Genres</Label>
                        <div className="mt-2">
                          <GenreSuggestionsList
                            suggestions={suggestions}
                            isLoading={isDetecting}
                            creationStatus={creationStatus}
                            showConfidence={false}
                            showCategory={false}
                            maxDisplay={3}
                            emptyMessage="No genres detected for this song"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Genre information helps visitors discover songs in your display
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      {isSongComplete(song) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setExpandedIndex(null)}
                        >
                          Collapse
                        </Button>
                      )}
                      {songs.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSong(index)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {songs.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No songs added yet</p>
          </div>
        )}

        {/* Add Song Button at Bottom */}
        <Button
          type="button"
          onClick={addSong}
          disabled={songs.length >= maxSongs}
          variant="outline"
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Song
        </Button>
      </CardContent>
    </Card>
  );
};

export default SongsSection;